import type { Scene } from '@/types';
import type { SceneData, AdditionalTimeField } from '@/types/application';

export class SceneService {
  createScenesFromImages(images: { id: string; imageUrl: string }[]): SceneData[] {
    return images.map((img) => ({
      id: img.id,
      imageUrl: img.imageUrl,
    }));
  }

  updateSceneText(scenes: Scene[], sceneId: string, text: string): Scene[] {
    return scenes.map((scene) =>
      scene.id === sceneId ? { ...scene, text } : scene
    );
  }

  updateSceneAdditionalTime(
    scenes: Scene[],
    sceneId: string,
    field: AdditionalTimeField,
    time: number
  ): Scene[] {
    return scenes.map((scene) => {
      if (scene.id !== sceneId) return scene;

      if (field === 'before') {
        return { ...scene, additionalDisplayTimeBefore: time };
      } else {
        return { ...scene, additionalDisplayTimeAfter: time };
      }
    });
  }

  applyTextsToScenes(scenes: Scene[], texts: string[]): Scene[] {
    if (texts.length !== scenes.length) {
      throw new Error(
        `テキスト数（${texts.length}）とシーン数（${scenes.length}）が一致しません`
      );
    }

    return scenes.map((scene, index) => ({
      ...scene,
      text: texts[index],
    }));
  }

  reorderScenes(scenes: Scene[], sceneIds: string[]): Scene[] {
    return sceneIds.map((id, index) => {
      const scene = scenes.find((s) => s.id === id);
      if (!scene) {
        throw new Error(`シーンID ${id} が見つかりません`);
      }
      return { ...scene, order: index };
    });
  }

  deleteScene(scenes: Scene[], sceneId: string): Scene[] {
    const filteredScenes = scenes.filter((s) => s.id !== sceneId);
    return filteredScenes.map((scene, index) => ({
      ...scene,
      order: index,
    }));
  }

  getSortedScenes(scenes: Scene[]): Scene[] {
    return [...scenes].sort((a, b) => a.order - b.order);
  }

  validateScenes(scenes: Scene[]): { valid: boolean; message?: string } {
    if (scenes.length === 0) {
      return { valid: false, message: 'シーンが1つも登録されていません' };
    }

    const missingTexts = scenes.filter((s) => !s.text);
    if (missingTexts.length > 0) {
      return {
        valid: false,
        message: `${missingTexts.length}個のシーンにテキストが設定されていません`,
      };
    }

    return { valid: true };
  }
}
